import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../../../constants';
import { TranslationModule } from '../../../../../i18n/translation-module';
import { ActivityInterface } from '@vendasta/crm';
import { CommonModule } from '@angular/common';
import { Component, computed, Inject, model, OnInit, Signal } from '@angular/core';
import { CrmFieldService, StandardIds } from '../../../../../shared-services/crm-services/field.service';
import { MatCardModule } from '@angular/material/card';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../../../tokens-and-interfaces';
import { ActivityCardComponent } from './activity-card/activity-card.component';
import { RouterLink } from '@angular/router';
import { ActivityAssociationService, ActivityNoteIcons, ActivitySource } from '../../../activities';
import { SanitizeHtmlPipe } from '../../../activities/sanitize-html.pipe';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';

@Component({
  selector: 'crm-activity-note',
  templateUrl: './note.component.html',
  styleUrls: ['./note.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    RouterLink,
    TranslationModule,
    MatCardModule,
    GalaxyAvatarModule,
    ActivityCardComponent,
    SanitizeHtmlPipe,
    GalaxyPipesModule,
  ],
})
export class CrmActivityNoteComponent implements OnInit {
  activity = model<ActivityInterface>({});
  associatedObjectId = model<string>('');
  objectType = model<ObjectType>('Contact');
  showPrimaryAssociation = computed(() => !this.associatedObjectId());

  isFormActivity = computed(() => {
    const activitySource = this.getStringFieldData(this.activity(), StandardIds.ActivitySourceName);
    return activitySource === ActivitySource.Form;
  });

  isListActivity = computed(() => {
    const activitySource = this.getStringFieldData(this.activity(), StandardIds.ActivitySourceName);
    return activitySource === ActivitySource.List;
  });

  namespace = toSignal(this.config.namespace$);

  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;
  primaryAssociationLink = computed(() => {
    if (!this.primaryAssociation()) return '';
    const linkUrl = this.primaryAssociation()?.linkUrl;
    return `${this.routePrefix()}/${linkUrl}`;
  });

  useBusinessCenterLink = toSignal(
    this.config.parentNamespace$.pipe(
      map((parentNamespace) => {
        return parentNamespace !== '';
      }),
    ),
  );

  icon = computed(() => {
    const activitySource = this.getStringFieldData(this.activity(), StandardIds.ActivitySourceName);
    return ActivityNoteIcons[activitySource] ?? ActivityNoteIcons['default'];
  });

  showAssociationPopover = computed(() => this.isFormActivity());

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      body: this.getStringFieldData(this.activity(), StandardIds.ActivityNoteBody),
      title: this.getStringFieldData(this.activity(), StandardIds.ActivityNoteTitle),
      url: this.getActivityUrl(),
      urlName: this.getActivityUrlName(),
    };
  });

  headerText = computed(() => {
    const activitySource = this.getStringFieldData(this.activity(), StandardIds.ActivitySourceName);
    const { title } = this.cardInfo();
    switch (activitySource) {
      case ActivitySource.Form:
        return this.translateService.instant('ACTIVITY.NOTE.FORM_SUBMITTED');
      case ActivitySource.List:
        return this.translateService.instant('ACTIVITY.NOTE.LIST.LIST_MEMBERSHIP');
      default:
        return title;
    }
  });

  constructor(
    private readonly fieldService: CrmFieldService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly associationService: ActivityAssociationService,
    private readonly translateService: TranslateService,
  ) {}

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;

  ngOnInit(): void {
    this.associationService.activity = this.activity();
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getActivityUrl(): string | null {
    return this.isFormActivity()
      ? this.getFormUrl()
      : this.getStringFieldData(this.activity(), StandardIds.ActivityUrl);
  }

  private getFormUrl(): string | null {
    const formID = this.getStringFieldData(this.activity(), StandardIds.ActivityRecordSourceDrillDown2);
    if (formID === '' || !this.isFormActivity()) {
      return null;
    }
    const link = `/custom-forms/edit/${formID}`;
    if (this.useBusinessCenterLink()) {
      return `/account/location/${this.namespace()}${link}`;
    }
    return link;
  }

  private getActivityUrlName(): string {
    return this.isFormActivity()
      ? this.getStringFieldData(this.activity(), StandardIds.ActivityRecordSourceDrillDown1)
      : this.getStringFieldData(this.activity(), StandardIds.ActivityUrlName);
  }

  subtitleForListActivity = computed(() => {
    const listMembershipStatus = this.getStringFieldData(this.activity(), StandardIds.ActivityRecordSourceDrillDown1);
    if (!this.isListActivity() || !listMembershipStatus) return;
    switch (listMembershipStatus) {
      case 'Removed from list':
        return this.translateService.instant('ACTIVITY.NOTE.LIST.REMOVED_FROM');
      case 'Added to list':
        return this.translateService.instant('ACTIVITY.NOTE.LIST.ADDED_TO');
    }
    return;
  });
}

interface ActivityCardInformation {
  body: string;
  title: string;
  url: string | null;
  urlName: string;
}
